import React, { useState, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import { Switch, Route, useHistory } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import TrackContext from '../../contexts/track-context';
import AdminSidebar from './AdminSidebar';
import Dashboard from './Dashboard';
import SubscriberWorld from './SubscriberWorld';
import CorporatePortal from './CorporatePortal';
import SuperAdminInsights from './SuperAdminInsights';
import Profile from '../Profile';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import UserWorld from './UserWorld';
import ErrorBoundary from '../../pure-components/ErrorBoundary';
import { ReactComponent as Logo } from '../../assets/Logo1.svg';
import Calendar from './Calendar';
import LicenseInsight from './licenseInsight';
import ActionRecords from '../ActionRecords';
import Projects from '../Projects';
import HarmixLibrary from '../Harmix/library';
import SFX from '../SoundFx/sfx';
import NewInspiration from '../NewInspiration';
import Catalog from '../Catalog';
import Support from '../Support';
import BlogListing from '../Blog/BlogListing';

// Constants
const USER_TYPES = {
  ADMIN: 'ADMIN',
  SUPER_ADMIN: 'SUPER_ADMIN',
};

const SECTION_TITLES = {
  dashboard: 'Dashboard',
  'user-world': 'User World',
  'corporate-portal': 'Corporate World',
  'subscriber-world': 'Subscriber World',
  'Admin-Insights': 'Admin Insights',
  insights: 'License Insights',
  Calendar: 'Calendar',
  profile: 'My Profile',

  "My-World" : "My World",
};

// Layout configuration
const LAYOUT_CONFIG = {
  sidebar: {
    width: {
      mobile: '85%',
      tablet: '320px',
      desktop: '20%',
      minWidth: '280px',
      maxWidth: '400px'
    }
  },
  content: {
    padding: {
      mobile: '4%',
      tablet: '3%',
      desktop: '2%'
    }
  },
  header: {
    height: '72px',
    padding: '0 4%'
  }
};

// Fallback component for error boundary
const ErrorFallback = ({ error, resetErrorBoundary }) => (
  <div className="flex flex-col items-center justify-center min-h-screen p-[4%] bg-gray-50">
    <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-[6%] text-center">
      <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-red-600 mb-2">Something went wrong</h2>
      <p className="text-gray-600 mb-4">{error.message}</p>
      <button
        onClick={resetErrorBoundary}
        className="px-[8%] py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        Try Again
      </button>
    </div>
  </div>
);

// Custom hook for user permissions
const useUserPermissions = (userDetails) => {
  return useMemo(() => {
    const userType = userDetails?.userType;
    const userTypes = JSON.parse(process.env.REACT_APP_USER_TYPE || '{}');
    
    return {
      isAdmin: userType === userTypes?.ADMIN,
      isSuperAdmin: userType === userTypes?.SUPER_ADMIN,
      userName:
        userDetails?.first_name ||
        userDetails?.name ||
        userDetails?.email ||
        (userType === userTypes?.SUPER_ADMIN ? 'Superadmin' : 'Admin'),
    };
  }, [userDetails]);
};

const AdminDashboard = () => {
  const contextData = useContext(TrackContext);
  const userDetails = contextData?.userDetails;
  const history = useHistory();
  
  const { isAdmin, isSuperAdmin, userName } = useUserPermissions(userDetails);
  
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  
  const profileRef = useRef(null);
  const sidebarRef = useRef(null);

  // Sidebar sections with memoization
  const sidebarSections = useMemo(() => {
    const baseSections = [{ key: 'Dashboard', value: 'dashboard' }];
    const myWorldSections = [{ key: 'My World', value: 'My-World' }];
    
    if (isAdmin) {
      return [
        ...baseSections,
        { key: 'User World', value: 'user-world' },
        { key: 'License Insights', value: 'insights' },
        ...myWorldSections
      ];
    }
    
    if (isSuperAdmin) {
      return [
        ...baseSections,
        { key: 'Corporate World', value: 'corporate-portal' },
        { key: 'Subscriber World', value: 'subscriber-world' },
        { key: 'Admin Insights', value: 'Admin-Insights' },
        { key: 'Calendar', value: 'Calendar' },
        { key: 'Blog', value: 'Blog' },
      ];
    }
    
    return baseSections;
  }, [isAdmin, isSuperAdmin]);

  // Map sections to routes
  const sectionRoutes = useMemo(
    () => ({
      dashboard: '/admin',
      'user-world': '/admin/userworld',
      'corporate-portal': '/admin/corporate',
      'subscriber-world': '/admin/subscribers',
      'Admin-Insights': '/admin/admininsights',
      insights: '/admin/insights',
      Calendar: '/admin/calendar',
      profile: '/admin/profile',

      "My-World": "/projects",
    }),
    []
  );

  // Handle section change with routing
  const handleSectionChange = useCallback((section) => {
    setActiveSection(section);
    history.push(sectionRoutes[section]);
  }, [history, sectionRoutes]);

  // Set active section based on current route (this handles reloads correctly)
  useEffect(() => {
    const currentPath = history.location.pathname;
    const section = Object.keys(sectionRoutes).find(
      (key) => sectionRoutes[key] === currentPath
    ) || 'dashboard';
    setActiveSection(section);

    // If no valid section found, redirect to dashboard to avoid blank states
    if (section === 'dashboard' && currentPath !== '/admin') {
      history.push('/admin');
    }
  }, [history.location.pathname, sectionRoutes, history]);  // Added 'history' to deps for safety

  // Event handlers
  const handleSignOut = useCallback(async () => {
    try {
      setIsSigningOut(true);
      if (window?.localStorage) window.localStorage.clear();
      if (window?.sessionStorage) window.sessionStorage.clear();
      document.cookie = 'x-access-token=; Max-Age=0; path=/;';
      document.cookie = 'user-data=; Max-Age=0; path=/;';
      await new Promise((resolve) => setTimeout(resolve, 500));
      // window.location.href = '/signin';
      window.location.href = '/login';
    } catch (error) {
      console.error('Sign out error:', error);
      // window.location.href = '/signin';
      window.location.href = '/login';
    } finally {
      setIsSigningOut(false);
    }
  }, []);

  const toggleDropdown = useCallback(() => {
    setDropdownOpen((prev) => !prev);
  }, []);

  const handleProfileClick = useCallback(() => {
    setActiveSection('profile');
    history.push('/admin/profile');
    setDropdownOpen(false);
  }, [history]);

  const handleSignOutClick = useCallback(() => {
    setShowSignOutModal(true);
    setDropdownOpen(false);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isSidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        window.innerWidth < 768
      ) {
        setIsSidebarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSidebarOpen]);

  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  // Section components mapping
  const sectionComponents = useMemo(
    () => ({
      dashboard: <Dashboard activeSection={activeSection} setActiveSection={handleSectionChange} />,
      'user-world': <UserWorld />,
      'corporate-portal': <CorporatePortal />,
      'subscriber-world': <SubscriberWorld />,
      'Admin-Insights': <SuperAdminInsights />,
      insights: <LicenseInsight />,
      Calendar: <Calendar />,
      profile: <Profile />,
      'Blog': <BlogListing />,

      'Music': <HarmixLibrary />,
      'Projects': <Projects />,
      'Play-History': <ActionRecords />,
      'Favorites': <ActionRecords />,
      'SFX': <SFX />,
      'Now-Out': <NewInspiration />,
      'Labels': <Catalog />,
      'Support': <Support />
    }),
    [activeSection, handleSectionChange]
  );

  if (userDetails && !isAdmin && !isSuperAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 px-[4%]">
        <div className="text-center w-full max-w-md">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="flex w-full min-h-screen bg-gray-100">
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={closeSidebar}
            aria-hidden="true"
          />
        )}
        <aside
          ref={sidebarRef}
          className={`
            fixed inset-y-0 left-0 z-50 bg-white shadow-lg transform transition-transform duration-300 ease-in-out
            w-[85%] sm:w-[320px]
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            lg:relative lg:translate-x-0 lg:w-[280px] xl:w-[320px] 2xl:w-[20%]
            border-r border-gray-200 flex flex-col flex-shrink-0
            min-w-[280px] max-w-[85vw] lg:max-w-none
          `}
          aria-label="Main navigation"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 min-h-20">
            <Logo className="w-[70%] max-w-[144px] h-auto" alt="Admin Panel Logo" />
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={closeSidebar}
              aria-label="Close sidebar"
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <AdminSidebar
              activeSection={activeSection}
              setActiveSection={handleSectionChange}
              sections={sidebarSections}
            />
          </div>
        </aside>
        <main className="flex flex-col min-h-screen flex-1 min-w-0">
          <header className="flex justify-between items-center w-full h-20 px-4 sm:px-6 bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center flex-1 min-w-0">
              <button
                className="lg:hidden mr-3 p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex-shrink-0"
                onClick={toggleSidebar}
                aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
              >
                <MenuIcon fontSize="small" />
              </button>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800">
                {SECTION_TITLES[activeSection] || activeSection.replace('-', ' ')}
              </h1>
            </div>
            <div ref={profileRef} className="relative flex-shrink-0">
              <button
                className="flex items-center gap-2 sm:gap-3 bg-white px-3 sm:px-4 py-2 rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-gray-300 transition-all duration-200"
                onClick={toggleDropdown}
                aria-label="Profile menu"
                aria-haspopup="true"
                aria-expanded={dropdownOpen}
              >
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium text-sm">
                  {userName.charAt(0).toUpperCase()}
                </span>
                <span className="font-medium text-gray-700 text-sm max-w-[80px] sm:max-w-32 truncate hidden sm:block">
                  {userName}
                </span>
                <svg
                  className={`transition-transform duration-200 text-gray-500 ${dropdownOpen ? 'rotate-180' : ''}`}
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div
                className={`absolute right-0 mt-2 w-56 space-y-2 py-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 transition-all duration-300 transform ${
                  dropdownOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                }`}
                role="menu"
                aria-hidden={!dropdownOpen}
              >
                {/* User info in dropdown with icons and improved design */}
                <div className="px-4 pt-4 pb-3 border-b border-gray-100 text-left flex flex-col gap-2">
                  <div className="flex items-center text-gray-900">
                    <PersonIcon className="text-blue-600 mr-2" style={{ fontSize: 22 }} />
                    <span className="font-bold text-sm">{userName}</span>
                  </div>
                  {userDetails?.email && (
                    <div className="flex items-center text-gray-600">
                      <EmailIcon className="text-blue-400 mr-2" style={{ fontSize: 20 }} />
                      <span className="text-sm break-all">{userDetails.email}</span>
                    </div>
                  )}
                </div>
                <div className="px-1">
                  <div 
                    className="flex items-center w-full py-1.5 px-3 hover:bg-blue-50 rounded cursor-pointer transition-colors group"
                    onClick={handleProfileClick}
                    role="menuitem"
                  >
                    <div className="w-5 flex items-center justify-center">
                      <EditIcon className="text-gray-700 group-hover:text-blue-600" style={{ fontSize: 21 }} />
                    </div>
                    <span className="ml-2.5 text-sm text-gray-700 group-hover:text-blue-600 font-medium">Edit Profile</span>
                  </div>
                </div>
                <div className="px-1">
                  <div 
                    className="flex items-center w-full py-1.5 px-3 hover:bg-red-50 rounded cursor-pointer transition-colors group"
                    onClick={handleSignOutClick}
                    role="menuitem"
                  >
                    <div className="w-5 flex items-center justify-center">
                      <LogoutIcon className="text-gray-700 group-hover:text-red-600" style={{ fontSize: 21 }} />
                    </div>
                    <span className="ml-2.5 text-sm text-gray-700 group-hover:text-red-600 font-medium">Sign Out</span>
                  </div>
                </div>
              </div>
            </div>
          </header>
          <div className="flex-1 bg-gray-50 p-2 sm:p-4">
            <Switch>
              <Route
                exact
                path="/admin"
                render={() => (
                  <div className="min-h-full">
                    {sectionComponents['dashboard']}
                  </div>
                )}
              />
              <Route
                path="/admin/userworld"
                render={() => (
                  <div className="min-h-full">
                    {sectionComponents['user-world']}
                  </div>
                )}
              />
              <Route
                path="/admin/corporate"
                render={() => (
                  <div className="min-h-full">
                    {sectionComponents['corporate-portal']}
                  </div>
                )}
              />
              <Route
                path="/admin/subscribers"
                render={() => (
                  <div className="min-h-full">
                    {sectionComponents['subscriber-world']}
                  </div>
                )}
              />
              <Route
                path="/admin/admininsights"
                render={() => (
                  <div className="min-h-full">
                    {sectionComponents['Admin-Insights']}
                  </div>
                )}
              />
              <Route
                path="/admin/insights"
                render={() => (
                  <div className="min-h-full">
                    {sectionComponents['insights']}
                  </div>
                )}
              />
              <Route
                path="/admin/calendar"
                render={() => (
                  <div className="min-h-full">
                    {sectionComponents['Calendar']}
                  </div>
                )}
              />
              <Route
                path="/admin/profile"
                render={() => (
                  <div className="min-h-full">
                    {sectionComponents['profile']}
                  </div>
                )}
              />
            </Switch>
          </div>
        </main>
      </div>
      {showSignOutModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60 transition-all duration-500 px-[4%] animate-in fade-in"
          role="dialog"
          aria-modal="true"
          aria-labelledby="signout-title"
        >
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md transform transition-all duration-500 scale-100 hover:scale-[1.02] animate-in slide-in-from-bottom-4 zoom-in-95 relative">
            <button
              onClick={() => setShowSignOutModal(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 group border-none"
              aria-label="Close modal"
            >
              <CloseIcon className="text-gray-400 group-hover:text-gray-600 transition-colors" fontSize="small" />
            </button>
            <div className="text-center">
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 bg-gradient-to-br from-red-400 to-red-600 rounded-full opacity-20 animate-pulse"></div>
                <div className="relative w-full h-full bg-gradient-to-br from-red-50 to-red-100 rounded-full flex items-center justify-center border-4 border-red-100 shadow-lg transform transition-all duration-300 hover:scale-110 hover:rotate-2">
                  <LogoutIcon className="text-red-600" fontSize="large" />
                </div>
              </div>
              <h2
                id="signout-title"
                className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3 tracking-tight"
              >
                Sign Out
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed max-w-sm mx-auto">
                Are you sure you want to sign out?
                <span className="block text-sm text-gray-500 mt-2 font-medium">
                  You'll need to log in again to access your account.
                </span>
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                <button
                  className="flex-1 px-[8%] sm:px-6 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold hover:shadow-md focus:outline-none focus:ring-4 focus:ring-gray-200 transition-all duration-300 disabled:opacity-50 transform hover:scale-[1.02] active:scale-[0.98] border border-gray-200"
                  onClick={() => setShowSignOutModal(false)}
                  disabled={isSigningOut}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 px-[8%] sm:px-6 py-3 rounded-xl bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-red-200 transition-all duration-300 disabled:opacity-70 flex items-center justify-center gap-2 transform hover:scale-[1.02] active:scale-[0.98] shadow-md min-h-[48px]"
                  onClick={handleSignOut}
                  disabled={isSigningOut}
                >
                  {isSigningOut && (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                  )}
                  <span className={isSigningOut ? 'animate-pulse' : ''}>
                    {isSigningOut ? 'Signing Out...' : 'Sign Out'}
                  </span>
                </button>
              </div>
            </div>
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-red-400 to-transparent rounded-full opacity-60"></div>
            <div className="absolute top-3 left-3 w-2 h-2 bg-red-200 rounded-full opacity-40"></div>
            <div className="absolute bottom-3 right-3 w-1 h-1 bg-red-300 rounded-full opacity-60"></div>
          </div>
        </div>
        
      )}
    </ErrorBoundary>
  );
};

export default AdminDashboard;
